// RealityAI Agent UI — AgentDetail (PHASE K7).
// Configure (PATCH) + run start (POST /runs) + history (GET /runs).
//
// Error UX (K7 §11):
//   403 — entitlement/access (no internal policy details);
//   404 — not found / disabled (generic, no enumeration);
//   409 — agent archived (cannot start a run);
//   429 — K5 limit: render the approved message + retry_after;
//   501 — not available in MVP (never faked).

import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Play, Check, X, Archive, History } from "lucide-react";
import {
  Agent,
  AgentRun,
  updateAgent,
  createRun,
  fetchAgentRuns,
  isTerminalStatus,
  agentErrorInfo,
  AgentErrorInfo,
} from "@/lib/api/agents";
import { formatDate, runStatusLabel } from "./types";
import { cn } from "@/lib/utils";

interface AgentDetailProps {
  agent: Agent;
  modelNames: string[];
  modelDisplayNames: Record<string, string>;
  onChanged: (a: Agent) => void;
  onOpenRun: (run: AgentRun) => void;
}

type FormState = {
  mode: "view" | "edit";
  name: string;
  description: string;
  instructions: string;
  busy: boolean;
  error: string | null;
};

export function AgentDetail({
  agent,
  modelNames,
  modelDisplayNames,
  onChanged,
  onOpenRun,
}: AgentDetailProps) {
  const [form, setForm] = useState<FormState>({
    mode: "view",
    name: agent.name,
    description: agent.description ?? "",
    instructions: agent.instructions ?? "",
    busy: false,
    error: null,
  });
  const [runs, setRuns] = useState<AgentRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [runsError, setRunsError] = useState<string | null>(null);
  // Run start form
  const [task, setTask] = useState("");
  const [modelOverride, setModelOverride] = useState("");
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [startRetryAfter, setStartRetryAfter] = useState<number | null>(null);

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    setRunsError(null);
    try {
      setRuns(await fetchAgentRuns(agent.id, { limit: 50 }));
    } catch (e) {
      const info = agentErrorInfo(e);
      setRunsError(info.detail || "Не удалось загрузить историю");
    } finally {
      setRunsLoading(false);
    }
  }, [agent.id]);

  useEffect(() => {
    void loadRuns();
  }, [loadRuns]);

  const startRun = useCallback(async () => {
    const trimmed = task.trim();
    if (!trimmed) {
      setStartError("Опишите задачу для агента");
      return;
    }
    setStarting(true);
    setStartError(null);
    setStartRetryAfter(null);
    try {
      const res = await createRun(agent.id, trimmed, modelOverride || undefined);
      setTask("");
      // Observe the fresh run: the run view connects to SSE immediately
      // (replay covers the queued→started events written by the scheduler).
      const run: AgentRun = {
        id: res.run_id,
        agent_id: agent.id,
        agent_name: agent.name,
        project_id: agent.project_id,
        task: trimmed,
        status: (res.status as AgentRun["status"]) || "queued",
        current_step: 0,
        started_at: null,
        finished_at: null,
        duration_seconds: null,
        usage: {},
        result: null,
        error: null,
        created_at: new Date().toISOString(),
      };
      onOpenRun(run);
    } catch (e) {
      const info = agentErrorInfo(e);
      renderRunError(info);
    } finally {
      setStarting(false);
    }
  }, [agent, modelOverride, onOpenRun, task]);

  const renderRunError = (info: AgentErrorInfo) => {
    setStartRetryAfter(null);
    if (info.status === 403) {
      setStartError("Нет доступа к этому агенту или модели");
    } else if (info.status === 404) {
      setStartError("Агент не найден");
    } else if (info.status === 409) {
      setStartError("Агент в архиве — запустить его нельзя");
    } else if (info.status === 422) {
      setStartError(info.detail || "Задача не может быть пуста");
    } else if (info.status === 429) {
      setStartError(info.detail || "Превышен лимит запусков");
      setStartRetryAfter(info.retryAfter ?? null);
    } else if (info.status === 501) {
      setStartError("Функция пока не доступна");
    } else {
      setStartError(info.detail || "Не удалось запустить агента");
    }
  };

  const save = useCallback(async () => {
    const name = form.name.trim();
    if (!name) {
      setForm((f) => ({ ...f, error: "Укажите название" }));
      return;
    }
    setForm((f) => ({ ...f, busy: true, error: null }));
    try {
      const updated = await updateAgent(agent.id, {
        name,
        description: form.description.trim() || null,
        instructions: form.instructions.trim(),
      });
      onChanged(updated);
      setForm({ mode: "view", name: updated.name, description: updated.description ?? "", instructions: updated.instructions ?? "", busy: false, error: null });
    } catch (e) {
      const info = agentErrorInfo(e);
      setForm((f) => ({
        ...f,
        busy: false,
        error: info.status === 403 ? "Нет доступа к этому агенту" : info.detail || "Не удалось сохранить",
      }));
    }
  }, [agent.id, form, onChanged]);

  const toggleArchive = useCallback(async () => {
    setForm((f) => ({ ...f, busy: true }));
    try {
      const updated = await updateAgent(agent.id, {
        status: agent.status === "active" ? "archived" : "active",
      });
      onChanged(updated);
    } catch {
      setForm((f) => ({ ...f, busy: false, error: "Не удалось изменить статус" }));
      return;
    }
    setForm((f) => ({ ...f, busy: false, error: null }));
  }, [agent, onChanged]);

  const modelOptions = useMemo(
    () =>
      modelNames.map((n) => ({ name: n, display: modelDisplayNames?.[n] || n })),
    [modelNames, modelDisplayNames]
  );

  const isActive = agent.status === "active";

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-6 space-y-6">
        {/* Identity + configure */}
        <section>
          {form.mode === "view" ? (
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl font-medium text-primary">{agent.name}</h2>
                  <span
                    className={cn(
                      "text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border",
                      isActive
                        ? "border-accent/40 text-accent"
                        : "border-hairline text-tertiary"
                    )}
                  >
                    {isActive ? "активен" : "архив"}
                  </span>
                </div>
                {agent.description && (
                  <p className="mt-1 text-sm text-secondary">{agent.description}</p>
                )}
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button
                  onClick={() =>
                    setForm((f) => ({ ...f, mode: "edit", error: null }))
                  }
                  className="h-8 px-3 rounded-lg text-xs text-secondary hover:bg-hoverbg hover:text-primary transition-colors flex items-center gap-1.5"
                >
                  <Pencil size={12} /> Изменить
                </button>
                <button
                  onClick={() => void toggleArchive()}
                  disabled={form.busy}
                  className="h-8 px-3 rounded-lg text-xs text-secondary hover:bg-hoverbg hover:text-primary transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Archive size={12} /> {isActive ? "В архив" : "Восстановить"}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-secondary mb-1">Название</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  maxLength={120}
                  className="w-full h-9 px-3 rounded-lg bg-surface border border-hairline text-sm text-primary focus:outline-none focus:border-accent"
                />
              </div>
              <div>
                <label className="block text-xs text-secondary mb-1">Описание</label>
                <input
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  maxLength={2000}
                  className="w-full h-9 px-3 rounded-lg bg-surface border border-hairline text-sm text-primary focus:outline-none focus:border-accent"
                />
              </div>
              <div>
                <label className="block text-xs text-secondary mb-1">Инструкции</label>
                <textarea
                  value={form.instructions}
                  onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value }))}
                  rows={4}
                  maxLength={20000}
                  className="w-full px-3 py-2 rounded-lg bg-surface border border-hairline text-sm text-primary focus:outline-none focus:border-accent resize-y"
                />
              </div>
              {form.error && <div className="text-sm text-red-400">{form.error}</div>}
              <div className="flex gap-2">
                <button
                  onClick={() => void save()}
                  disabled={form.busy}
                  className="h-9 px-4 rounded-lg text-sm font-medium bg-accent/15 text-accent hover:bg-accent/25 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Check size={14} /> {form.busy ? "Сохранение…" : "Сохранить"}
                </button>
                <button
                  onClick={() =>
                    setForm({ mode: "view", name: agent.name, description: agent.description ?? "", instructions: agent.instructions ?? "", busy: false, error: null })
                  }
                  className="h-9 px-4 rounded-lg text-sm text-secondary hover:bg-hoverbg2 hover:text-primary transition-colors flex items-center gap-1.5"
                >
                  <X size={14} /> Отмена
                </button>
              </div>
            </div>
          )}
          <p className="mt-3 text-[11px] text-tertiary">
            Создан {formatDate(agent.created_at)} · Обновлён {formatDate(agent.updated_at)}
          </p>
        </section>

        {/* Run start */}
        <section className="rounded-xl border border-hairline bg-surface p-4">
          <h3 className="text-sm font-medium text-primary flex items-center gap-2">
            <Play size={14} className="text-accent" /> Запустить агента
          </h3>
          <textarea
            value={task}
            onChange={(e) => setTask(e.target.value)}
            rows={3}
            placeholder="Задача для агента…"
            className="mt-3 w-full px-3 py-2 rounded-lg bg-app border border-hairline text-sm text-primary placeholder:text-tertiary focus:outline-none focus:border-accent resize-y"
          />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <select
              value={modelOverride}
              onChange={(e) => setModelOverride(e.target.value)}
              className="h-9 px-3 rounded-lg bg-app border border-hairline text-sm text-primary focus:outline-none focus:border-accent max-w-[240px]"
            >
              <option value="">Модель по умолчанию</option>
              {modelOptions.map((m) => (
                <option key={m.name} value={m.name}>
                  {m.display}
                </option>
              ))}
            </select>
            <button
              onClick={() => void startRun()}
              disabled={starting || !isActive}
              className="h-9 px-5 rounded-lg text-sm font-medium bg-accent/15 text-accent hover:bg-accent/25 transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              <Play size={14} /> {starting ? "Запуск…" : "Запустить"}
            </button>
            {!isActive && (
              <span className="text-xs text-tertiary">Агент в архиве — запуск недоступен</span>
            )}
          </div>
          {startError && (
            <div className="mt-3 text-sm text-red-400">
              {startError}
              {startRetryAfter != null && (
                <span className="text-secondary">
                  {" "}
                  (повторить через ~{formatRetryAfter(startRetryAfter)})
                </span>
              )}
            </div>
          )}
        </section>

        {/* History */}
        <section>
          <h3 className="text-sm font-medium text-primary flex items-center gap-2 mb-3">
            <History size={14} className="text-accent" /> История запусков
          </h3>
          {runsLoading && <div className="text-sm text-tertiary py-3 text-center">Загрузка…</div>}
          {!runsLoading && runsError && (
            <div className="text-sm text-red-400 py-3 text-center">{runsError}</div>
          )}
          {!runsLoading && !runsError && runs.length === 0 && (
            <div className="text-sm text-tertiary py-3 text-center">Запусков пока нет</div>
          )}
          {!runsLoading && !runsError && runs.length > 0 && (
            <div className="space-y-1.5">
              {runs.map((run) => (
                <button
                  key={run.id}
                  onClick={() => onOpenRun(run)}
                  className="w-full text-left rounded-lg border border-hairline bg-surface px-3 py-2.5 hover:border-accent/40 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <StatusDot status={run.status} />
                    <span className="text-sm text-primary truncate flex-1">{run.task}</span>
                    <span className="text-xs text-tertiary shrink-0">{runStatusLabel(run.status)}</span>
                    <span className="text-[11px] text-tertiary shrink-0">{formatDate(run.created_at)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function StatusDot({ status }: { status: string }) {
  const cls = isTerminalStatus(status)
    ? status === "completed"
      ? "bg-emerald-400"
      : status === "failed"
        ? "bg-red-400"
        : "bg-tertiary"
    : "bg-accent animate-pulse";
  return <span className={cn("w-2 h-2 rounded-full shrink-0", cls)} />;
}

export function formatRetryAfter(seconds: number): string {
  if (seconds < 60) return `${Math.ceil(seconds)} с`;
  return `${Math.ceil(seconds / 60)} мин`;
}
